import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import ravenLogo from '@/assets/raven-logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, user, perfil, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && perfil && !authLoading) {
      // Redirect based on role
      if (perfil.rol === 'super_admin') {
        navigate('/super-admin/empresas');
      } else {
        // admin, gestor, tecnico all go to dashboard
        navigate('/dashboard');
      }
    }
  }, [user, perfil, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await signIn(email, password);
      
      if (!error) {
        // Navigation will be handled by useEffect when perfil is loaded
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center login-background p-4 relative overflow-hidden">
      {/* Geometric background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, hsl(var(--primary)) 0px, transparent 1px, transparent 60px),
                           repeating-linear-gradient(90deg, hsl(var(--primary)) 0px, transparent 1px, transparent 60px)`
        }} />
      </div>

      {/* Theme Toggle */}
      <div className="absolute top-6 right-6 z-50">
        <ThemeToggle />
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-md login-card login-card-entrance border-border/30 relative z-10">
        <CardHeader className="text-center space-y-8 pb-8">
          {/* Logo Section */}
          <div className="flex justify-center">
            <div className="logo-container relative">
              {/* Glow effect behind logo */}
              <div className="logo-glow" />
              {/* Logo */}
              <img 
                src={ravenLogo} 
                alt="Raven Logo" 
                className="h-40 w-40 object-contain relative z-10 drop-shadow-2xl" 
              />
            </div>
          </div>

          {/* Title Section */}
          <div className="space-y-3">
            <CardTitle className="text-4xl font-display uppercase tracking-[0.2em] mb-3">
              <span className="login-title-gradient">Raven</span>
            </CardTitle>
            <div className="h-px w-24 mx-auto bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
            <CardDescription className="text-muted-foreground text-base mt-4">
              Sistema de Gestión Premium
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div className="space-y-2 login-input-cascade">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <div className="relative login-input-wrapper">
                <Mail className="login-input-icon h-4 w-4" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="transition-all duration-300"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2 login-input-cascade">
              <Label htmlFor="password" className="text-sm font-medium">Contraseña</Label>
              <div className="relative login-input-wrapper">
                <Lock className="login-input-icon h-4 w-4" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="transition-all duration-300"
                />
              </div>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              className="w-full mt-8 h-12 text-base font-semibold login-button-gradient border-0"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Accediendo...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>
        </CardContent>

        {/* Footer */}
        <div className="px-8 pb-6 text-center">
          <p className="text-xs text-muted-foreground/60">
            © 2024 Raven Systems
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Login;